import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, EventEmitter, Output, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { marked } from 'marked';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import Turndown from 'turndown';

@Component({
    selector: 'markdown-editor',
    templateUrl: 'markdown-editor.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarkdownEditorComponent {
  stateItalic = false;
  stateBold = false;
  stateBullet = false;
  stateNumbered = false;
  stateLink = false;
  currentLink: string = undefined;

  constructor(
    private alertCtrl: AlertController,
    private cdRef: ChangeDetectorRef,
  ) { }

  private turndownService = new Turndown();

  protected isReady = false;
  @ViewChild('editor', { static: true }) editor: ElementRef;

  @Input() data: string;
  @Input() readonly = false;
  @Input() height?;
  protected htmlData: string;
  @Output() updateData: EventEmitter<string> = new EventEmitter<string>();

  ngOnInit() {

    this.isReady = true; //!!!!!
    this.cdRef.markForCheck();

  }

  ngAfterViewInit() {
    if (this.data) {
      // This sanitizes old recipes that still have array type for instructions
      this.editor.nativeElement.innerHTML = typeof this.data === 'string' ? marked(this.data, { breaks: true, async: false }) : '';
      this.isReady = true;
    }
    this.cdRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editor && this.editor.nativeElement) {
      this.editor.nativeElement.contentEditable = !this.readonly;
    }
    if (changes.data && this.editor && this.editor.nativeElement) {
      // Re-render the markdown when readonly (the editor always mirrors the source), or
      // when the editor is still empty in edit mode. The empty check lets an external set
      // — e.g. the recipe scanner applying extracted instructions after the page is open —
      // populate a blank editor, while never clobbering content the user has already typed.
      const editorIsEmpty = !this.editor.nativeElement.textContent?.trim();
      if (this.readonly || editorIsEmpty) {
        // This sanitizes old recipes that still have array type for instructions
        this.editor.nativeElement.innerHTML = typeof this.data === 'string' ? marked(this.data, { breaks: true, async: false }) : '';
        this.isReady = true;
      }
    }
    this.cdRef.markForCheck();
  }

  handleInputChanged() {
    if (this.editor && this.editor.nativeElement) {
      this.updateData.emit(this.turndownService.turndown(this.editor.nativeElement.innerHTML));
    }
  }

  handleCaretMove() {
    const sel = window.getSelection();
    // if no selection or no ranges (e.g., readonly mode), skip
    if (!sel || sel.rangeCount === 0) {
      return;
    }
    let node = sel.getRangeAt(0).startContainer;
    this.stateItalic = false;
    this.stateBold = false;
    this.stateBullet = false;
    this.stateNumbered = false;
    this.stateLink = false;
    this.currentLink = undefined;

    while (node) {
      if (node.nodeName === "DIV" && (node as HTMLElement).className === "editor") {
        break;
      }
      if (node.nodeName === "I" || node.nodeName === "EM") {
        this.stateItalic = true;
      }
      if (node.nodeName === "B" || node.nodeName === "STRONG") {
        this.stateBold = true;
      }
      if (node.nodeName === "LI") {
        this.stateNumbered = true;
      }
      if (node.nodeName === "A") {
        this.stateLink = true;
        this.currentLink = (node as HTMLAnchorElement).href;
      }
      node = node.parentNode;
    }
    this.cdRef.markForCheck();
  }

  handleItalic() {
    document.execCommand('italic')
    if (this.stateItalic) {
      this.stateItalic = false;
    } else {
      this.stateItalic = true;
    }
    this.handleCaretMove();
  }
  handleBold() {
    document.execCommand('bold')
    if (this.stateBold) {
      this.stateBold = false;
    } else {
      this.stateBold = true;
    }
    this.handleCaretMove();
  }

  handleBullet() {
    document.execCommand('insertUnorderedList')
    if (this.stateBullet) {
      this.stateBullet = false;
    } else {
      this.stateBullet = true;
    }
    this.handleCaretMove();
  }

  handleNumbered() {
    document.execCommand('insertOrderedList')
    if (this.stateNumbered) {
      this.stateNumbered = false;
    } else {
      this.stateNumbered = true;
    }
    this.handleCaretMove();
  }

  async handleLink() {
    let selection = document.getSelection();

    let range = selection.getRangeAt(0)

    let link = await this.alertCtrl.showAlertAsync<false | null | string>({
      inputs: [
        {
          name: 'link',
          placeholder: LL.markdownEditor.placeholders.url(),
          type: 'url',
          value: this.currentLink
        },
      ],
      buttons: [
        { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: (data: { link: string }) => { return data.link; } },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.markdownEditor.buttons.unlink(), result: null }
      ]
    })
    selection.removeAllRanges();
    selection.addRange(range)

    if (link === null) {
      document.execCommand('unlink');
    } else if (typeof link === 'string') {
      document.execCommand('createLink', null, link);
    }

    this.handleCaretMove();
  }
}
